import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND } from "../data/portfolio";

const LINKS = [
  { label: "Work", to: "/#work" },
  { label: "Services", to: "/#services" },
  { label: "About", to: "/#about" },
];

const HEADER_INITIAL = { y: -80, opacity: 0 };
const HEADER_ANIMATE = { y: 0, opacity: 1 };
const HEADER_TRANSITION = { duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] };
const MENU_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const LINK_INITIAL = { opacity: 0, x: -20 };
const LINK_ANIMATE = { opacity: 1, x: 0 };

const MOBILE_LINKS = [...LINKS, { label: "Contact", to: "/#contact" }];

function MobileMenu({ open, onClose }) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...MENU_FADE}
          className="fixed inset-0 z-[70] bg-[#080808]/85 backdrop-blur-2xl md:hidden flex flex-col p-6 transform-gpu isolate"
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center h-14">
            <span className="font-display font-extrabold text-xl">{BRAND.name}.</span>
            <button onClick={onClose} data-testid="nav-menu-close" aria-label="Close menu">
              <X size={26} />
            </button>
          </div>
          <div className="flex flex-col gap-2 mt-16">
            {MOBILE_LINKS.map((l, i) => (
              <motion.div
                key={l.label}
                initial={LINK_INITIAL}
                animate={LINK_ANIMATE}
                transition={{ delay: 0.1 + i * 0.08 }}
              >
                <Link
                  to={l.to}
                  onClick={onClose}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className="block font-display text-5xl font-bold py-3 border-b border-white/10"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
          </div>
          <Link
            to="/#contact"
            onClick={onClose}
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-4 text-sm font-medium"
          >
            Start a project <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={HEADER_INITIAL}
        animate={HEADER_ANIMATE}
        transition={HEADER_TRANSITION}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="font-display font-extrabold text-xl tracking-tight">
            {BRAND.name}<span className="text-white/40">.</span>
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="link-underline text-sm text-white/70 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <Link
            to="/#contact"
            data-testid="nav-contact"
            className="lift hidden md:inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/20 backdrop-blur-md px-5 py-2.5 text-sm hover:bg-white hover:text-black transition-colors duration-300"
          >
            Contact
          </Link>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className="md:hidden p-2 text-white"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </nav>
      </motion.header>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
}
